import React, { useEffect, useMemo, useRef, useState } from 'react';
import OrderBlockedNotice from '../../components/OrderDocumentsStep/OrderBlockedNotice';
import OrderDocumentsStep, {
  documentsAnswered,
  needsAnswer,
} from '../../components/OrderDocumentsStep/OrderDocumentsStep';
import SubmittedDocuments from '../../components/OrderDocumentsStep/SubmittedDocuments';
import { useHistory } from 'react-router-dom';
import { useToast } from '../../components/Toast/ToastContext';
import { useExemptionDocumentUpload } from '../Account/useExemptionDocumentUpload';
import {
  rescueOrderDocumentRequirementsQuery,
  type RescueOrderDocumentRequirements,
  type RescueOrderDocumentRequirementsData,
  type RescueOrderDocumentRequirementsVariables,
} from '../../lib/documentQueries';
import { stateDocumentKind, stateName } from '../../lib/documentLabels';
import { SUPPORT_EMAIL } from '../../lib/emails';
import stepStyles from '../../components/OrderAttestationStep/orderStepModal.module.scss';
import { ResellerTaxExemptType } from '@/types/gql-op-types';
import { useMutation, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import type { AppState } from '../../../lib/store';
import { useRescueContext } from '../../lib/RescueContext';
import { EMAIL_RE, US_STATE_OPTIONS } from '../../lib/utils';
import {
  rescueCreateNanoOrderMutation,
  RescueCreateNanoOrderData,
  RescueCreateNanoOrderInput,
  rescueOrgInfoQuery,
  RescueOrgInfoData,
} from '../../lib/rescueQueries';
import { trackChipsOrderSubmitted } from '../../analytics';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import StepProgress from '../../components/StepProgress/StepProgress';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { FormGrid } from '../../components/FormField/FormGrid';
import Button from '../../components/Button/Button';
import styles from './Chips.module.scss';

const CHIPS_PER_BOX = 20;

// Box-count is the canonical unit: backend takes `boxQuantity` and bounds it
// 1–25. Chip counts shown to the user are derived (`boxes * CHIPS_PER_BOX`).
const BOX_PRESETS = ['1', '2', '3', '4', '5'] as const;
const QUANTITY_PRESETS = [...BOX_PRESETS, 'Other'] as const;
type QuantityChoice = (typeof QUANTITY_PRESETS)[number];
const DEFAULT_QUANTITY: QuantityChoice = '3';
const CUSTOM_BOX_MIN = 6;
const CUSTOM_BOX_MAX = 25;
const CUSTOM_BOX_STEP = 1;

interface OrderNanosModalProps {
  open: boolean;
  onClose: () => void;
}

interface SuccessState {
  orderId: string;
  boxCount: number;
}

/** Why this shipment needs documents, named for where it goes. */
function documentsIntro(requirements: RescueOrderDocumentRequirements, government: boolean): string {
  const where = requirements.shipToState ? stateName(requirements.shipToState) : 'this state';
  const letter = requirements.required.find((entry) => entry.documentType === 'NONPROFIT_LETTER');
  const certificate = requirements.required.find((entry) => entry.documentType === 'STATE_EXEMPTION');
  const certificateDoc = certificate?.document ?? null;
  const later = `This order won't be held up while we review it. Future orders to ${where} require document approval.`;

  if (!requirements.canOrder && certificateDoc?.status === 'PENDING') {
    return `Shipping to ${where} needs your ${where} exemption certificate approved. The Fi team is still reviewing it.`;
  }
  if (!requirements.canOrder && certificateDoc?.status === 'REJECTED') {
    return `The Fi team couldn't verify your ${where} exemption certificate. Orders to ${where} can be placed once they approve a replacement.`;
  }
  if (certificateDoc?.expired) {
    return `Your ${where} exemption certificate has expired. Upload a renewed one to ship to ${where}. ${later}`;
  }
  if (!certificate) {
    return government
      ? "Fi Nanos are free for nonprofits and government shelters, so we need proof of your shelter's government status on file. Your first Fi order won't be held up while we review this. Future orders require document approval."
      : "Fi Nanos are free for registered nonprofits, so we need your IRS determination letter on file. Your first Fi order won't be held up while we review this. Future orders require document approval.";
  }
  if (letter?.satisfied) {
    return `This is your first Fi Nano order shipping to ${where}, so we need a ${where} exemption certificate on file. ${later}`;
  }
  if (letter?.onFile) {
    return `Fi Nanos are free for registered nonprofits. Shipping to ${where} needs two documents on file. This order won't be held up while we review this. Future orders require document approval.`;
  }
  return `Shipping to ${where} needs two documents on file. Your first Fi order won't be held up while we review this. Future orders require document approval.`;
}

/** No upload clears these, so the step says so instead of offering one. */
function noticeText(requirements: RescueOrderDocumentRequirements): string | null {
  if (requirements.notices.length === 0) {
    return null;
  }
  const where = requirements.shipToState ? stateName(requirements.shipToState) : 'this address';
  return requirements.notices.some((notice) => notice.kind === 'JURISDICTION_UNSUPPORTED')
    ? `We can't take Fi Nano orders shipping to ${where} yet. Contact support and the Fi team will sort it out.`
    : 'We need to confirm a few details about your organization before this order can ship. Contact support and the Fi team will sort it out.';
}

export default function OrderNanosModal({ open, onClose }: OrderNanosModalProps) {
  const { orgName, shippingAddress, taxExemptType } = useRescueContext();
  const government = taxExemptType === ResellerTaxExemptType.GOVERNMENT_ENTITY;
  const session = useSelector((state: AppState) => state.session);
  // Shares the cache with RescueContext's query, so this adds no extra network request.
  const { data: orgData } = useQuery<RescueOrgInfoData>(rescueOrgInfoQuery, { errorPolicy: 'all' });
  // Pre-fill Contact Name from the chip reseller's stored contact, not the user account.
  const resellerContact = orgData?.currentUser?.chipReseller?.contactInfo;
  const resellerContactName = resellerContact
    ? [resellerContact.firstName, resellerContact.lastName].filter(Boolean).join(' ')
    : '';
  const [createOrder] = useMutation<RescueCreateNanoOrderData, { input: RescueCreateNanoOrderInput }>(
    rescueCreateNanoOrderMutation,
  );

  // Only pre-fill from the rescue's stored address when it's actually a clean
  // US address with a 2-letter state code that matches our `<select>` options.
  const validUSAddress = useMemo(() => {
    if (!shippingAddress) {
      return null;
    }
    if (shippingAddress.country !== 'US') {
      return null;
    }
    if (!shippingAddress.state || !US_STATE_OPTIONS.some((s) => s.abbreviation === shippingAddress.state)) {
      return null;
    }
    return shippingAddress;
  }, [shippingAddress]);

  const [contactName, setContactName] = useState(resellerContactName);
  const [contactEmail, setContactEmail] = useState(session?.email ?? '');
  const [quantityChoice, setQuantityChoice] = useState<QuantityChoice>(DEFAULT_QUANTITY);
  const [customBoxCount, setCustomBoxCount] = useState(CUSTOM_BOX_MIN);
  const [street, setStreet] = useState(validUSAddress?.line1 ?? '');
  const [apt, setApt] = useState(validUSAddress?.line2 ?? '');
  const [city, setCity] = useState(validUSAddress?.city ?? '');
  const [usState, setUsState] = useState(validUSAddress?.state ?? '');
  const [zip, setZip] = useState(validUSAddress?.zip ?? '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [onDocuments, setOnDocuments] = useState(false);
  // Fixed when the step opens, so an upload on it doesn't rewrite the sentence above it.
  const [stepIntro, setStepIntro] = useState('');

  // Keyed on where this order ships, not where the rescue sits: a certificate answers one
  // jurisdiction, so shipping somewhere new asks for that state's document. Asked before a state is
  // chosen too, since the letter check doesn't depend on one.
  const { data: requirementsData, refetch: refetchRequirements } = useQuery<
    RescueOrderDocumentRequirementsData,
    RescueOrderDocumentRequirementsVariables
  >(rescueOrderDocumentRequirementsQuery, {
    variables: { input: { program: 'FI_NANO', shipToState: usState || null } },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const requirements = requirementsData?.rescueOrderDocumentRequirements ?? null;

  const history = useHistory();

  const { showToast } = useToast();

  const [justReplaced, setJustReplaced] = useState(false);

  const { fileInputRef, handleFileSelected, pickFileFor, fileError, uploadingFor, keyFor } = useExemptionDocumentUpload(
    (documentType) => {
      void refetchRequirements();
      // Filed from inside this modal, so the wait that follows is news rather than a refusal.
      if (blocked) {
        setJustReplaced(true);
        showToast(
          `${documentType === 'NONPROFIT_LETTER' ? 'Letter' : stateDocumentKind(documentType)} uploaded. The Fi team will review it within a few business days.`,
        );
      }
    },
  );
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Re-seed the address fields once per open cycle so org-info that resolves
  // after the modal mounts still pre-fills the form. Mirrors OrderDevicesModal.
  const seededAddressRef = useRef(false);
  const seededEmailRef = useRef(false);
  const seededContactRef = useRef(false);
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededAddressRef.current = false;
      return;
    }
    if (validUSAddress && !seededAddressRef.current) {
      setStreet(validUSAddress.line1 ?? '');
      setApt(validUSAddress.line2 ?? '');
      setCity(validUSAddress.city ?? '');
      setUsState(validUSAddress.state ?? '');
      setZip(validUSAddress.zip ?? '');
      seededAddressRef.current = true;
    }
  }, [open, validUSAddress]);
  useEffect(() => {
    if (!open) {
      seededEmailRef.current = false;
      return;
    }
    if (session?.email && !seededEmailRef.current) {
      setContactEmail(session.email);
      seededEmailRef.current = true;
    }
  }, [open, session?.email]);
  // Re-seed Contact Name once the reseller's org info resolves. `contactName` is
  // initialized from resellerContactName, but that derives from an async query
  // that is often still empty at first render — without this the field would
  // stay blank when org info arrives after the modal mounts.
  useEffect(() => {
    if (!open) {
      seededContactRef.current = false;
      return;
    }
    if (resellerContactName && !seededContactRef.current) {
      setContactName(resellerContactName);
      seededContactRef.current = true;
    }
  }, [open, resellerContactName]);

  function resetForm() {
    setContactName(resellerContactName);
    setContactEmail(session?.email ?? '');
    setQuantityChoice(DEFAULT_QUANTITY);
    setCustomBoxCount(CUSTOM_BOX_MIN);
    setStreet(validUSAddress?.line1 ?? '');
    setApt(validUSAddress?.line2 ?? '');
    setCity(validUSAddress?.city ?? '');
    setUsState(validUSAddress?.state ?? '');
    setZip(validUSAddress?.zip ?? '');
    setNotes('');
    setShowErrors(false);
    setSubmitError(null);
    setSubmitting(false);
    setSuccess(null);
    submittingRef.current = false;
    setOnDocuments(false);
    setJustReplaced(false);
  }

  function handleClose() {
    if (submittingRef.current) {
      return;
    }
    resetForm();
    onClose();
  }

  const isOther = quantityChoice === 'Other';
  const boxCount = isOther ? customBoxCount : Number.parseInt(quantityChoice, 10);
  const chipCount = boxCount * CHIPS_PER_BOX;
  const trimmedEmail = contactEmail.trim();

  const errors = {
    contactName: contactName.trim() === '',
    contactEmailMissing: trimmedEmail === '',
    contactEmailInvalid: trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail),
    boxCount: !Number.isFinite(boxCount) || boxCount <= 0,
    street: street.trim() === '',
    city: city.trim() === '',
    state: usState === '',
    zip: zip.trim() === '',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  /** Outstanding documents the rescue can still act on, which is what the step offers. */
  const outstanding = (requirements?.required ?? []).filter(needsAnswer);
  const answered = documentsAnswered(requirements?.required ?? []);

  // The letter is checked before the form opens; a state certificate only once the ship-to is known,
  // so a refusal on that one is shown in the documents step, where changing the state can clear it.
  const blocked =
    requirements !== null && !requirements.canOrder && requirements.blockedBy.includes('NONPROFIT_LETTER');
  const stateBlocked = requirements !== null && !requirements.canOrder && !blocked;
  const needsDocuments = !blocked && (outstanding.length > 0 || stateBlocked);
  const where = requirements?.shipToState ? stateName(requirements.shipToState) : null;
  const waitNote =
    stateBlocked && where
      ? `Your next Fi Nano order to ${where} can be placed once the Fi team approves this certificate. To order now, ship to a state with an approved certificate.`
      : null;

  async function handleSubmit() {
    if (submittingRef.current) {
      return;
    }
    if (hasErrors) {
      setSubmitError(null);
      setShowErrors(true);
      return;
    }
    // The documents step is skipped when nothing is outstanding, so a rescue that has filed
    // everything never sees it.
    if (!onDocuments && needsDocuments) {
      setSubmitError(null);
      setStepIntro(requirements ? documentsIntro(requirements, government) : '');
      setOnDocuments(true);
      return;
    }
    submittingRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await createOrder({
        variables: {
          input: {
            contactName: contactName.trim(),
            contactEmail: trimmedEmail,
            boxQuantity: boxCount,
            shippingAddress: {
              line1: street.trim(),
              line2: apt.trim() ? apt.trim() : null,
              city: city.trim(),
              state: usState,
              country: 'US',
              zip: zip.trim(),
            },
            notes: notes.trim() ? notes.trim() : null,
          },
        },
      });
      const data = result.data?.rescueCreateNanoOrder;
      if (!data?.ok) {
        throw new Error('Order submission was not accepted');
      }
      trackChipsOrderSubmitted(chipCount);
      setSuccess({ orderId: data.orderId, boxCount });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order request');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function decrementCustom() {
    setCustomBoxCount((prev) => Math.max(CUSTOM_BOX_MIN, prev - CUSTOM_BOX_STEP));
  }

  function incrementCustom() {
    setCustomBoxCount((prev) => Math.min(CUSTOM_BOX_MAX, prev + CUSTOM_BOX_STEP));
  }

  if (success) {
    const { orderId, boxCount: confirmedBoxes } = success;
    const confirmedChips = confirmedBoxes * CHIPS_PER_BOX;
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <div className={styles.orderConfirmState}>
          <div className={styles.orderConfirmIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12.5L10 17.5L19 7.5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.orderConfirmTitle}>Request Submitted</h2>
          <p className={styles.orderConfirmDesc}>
            Your order for{' '}
            <strong>
              {confirmedChips} Fi Nano microchip{confirmedChips === 1 ? '' : 's'} ({confirmedBoxes} box
              {confirmedBoxes === 1 ? '' : 'es'})
            </strong>{' '}
            has been submitted for approval. You'll receive an email confirmation once approved.
          </p>
          <SubmittedDocuments required={requirements?.required ?? []} />

          <div className={styles.orderConfirmFooter}>
            <div className={styles.orderConfirmOrderNumber}>Order #{orderId}</div>
          </div>
        </div>
      </Modal>
    );
  }

  const customChipDisplay = customBoxCount * CHIPS_PER_BOX;

  if (blocked) {
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <OrderBlockedNotice
          required={requirements?.required ?? []}
          notices={requirements?.notices ?? []}
          shipToState={requirements?.shipToState ?? (usState || null)}
          uploadingKey={uploadingFor}
          keyFor={keyFor}
          onPickFile={pickFileFor}
          fileError={fileError}
          programName="Fi Nano"
          justReplaced={justReplaced}
        />
        <ModalActions>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          {/* They came here to be told; the account page is where they can act on it. A rejection is fixed right here. */}
          {!justReplaced && !requirements?.required.some((entry) => entry.document?.status === 'REJECTED') && (
            <Button variant="green" onClick={() => history.push('/rescue/account')}>
              View documents
            </Button>
          )}
        </ModalActions>
      </Modal>
    );
  }

  // Something on file is the bar, not approval: a first order ships while Fi reviews it. Leaving
  // Submit enabled would let the rescue walk past the step without uploading.
  const documentsActions = (
    <>
      <Button variant="secondary" onClick={() => setOnDocuments(false)} disabled={submitting}>
        &larr; Back
      </Button>
      <Button variant="green" onClick={handleSubmit} disabled={submitting || !answered || stateBlocked}>
        {submitting ? 'Submitting…' : 'Submit Request'}
      </Button>
    </>
  );

  let detailsLabel = 'Submit Request';
  if (submitting) {
    detailsLabel = 'Submitting…';
  } else if (needsDocuments) {
    detailsLabel = 'Continue →';
  }
  const detailsActions = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="green" onClick={handleSubmit} disabled={submitting}>
        {detailsLabel}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="wide"
      className={stepStyles.orderStepModal}
      dismissOnBackdropClick={false}
    >
      <div className={stepStyles.orderStepBody}>
        {onDocuments && (
          <>
            <StepProgress currentStep={2} totalSteps={2} labelSuffix="Documents" />
            <ModalTitle>Nonprofit documents</ModalTitle>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />
            {requirements && (
              <OrderDocumentsStep
                required={requirements.required}
                uploadingKey={uploadingFor}
                keyFor={keyFor}
                onPickFile={pickFileFor}
                fileError={fileError}
                intro={stepIntro}
                letterPrograms="Fi Minis and Fi Nanos"
                government={government}
                waitNote={waitNote}
                notice={noticeText(requirements)}
              />
            )}
          </>
        )}

        {!onDocuments && (
          <>
            {needsDocuments && <StepProgress currentStep={1} totalSteps={2} labelSuffix="Order details" />}
            <ModalTitle>Order Fi Nano Microchips</ModalTitle>
            <p className={styles.modalDescription}>
              Order free Fi Nano microchip boxes for <strong>{orgName || 'your rescue'}</strong>. Chips come in boxes of
              20.
            </p>

            <FormGrid>
              <PortalField
                label="Contact Name *"
                error="Please provide a contact name"
                showError={showErrors && errors.contactName}
              >
                <PortalInput
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Full name"
                  hasError={showErrors && errors.contactName}
                />
              </PortalField>
              <PortalField
                label="Contact Email *"
                error={
                  errors.contactEmailMissing ? 'Please provide a contact email' : 'Please provide a valid email address'
                }
                showError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
              >
                <PortalInput
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="you@example.org"
                  hasError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
                />
              </PortalField>
            </FormGrid>

            <PortalField label="Quantity *" hint={`${chipCount} chips = ${boxCount} box${boxCount === 1 ? '' : 'es'}`}>
              <ToggleGroup
                options={QUANTITY_PRESETS.map((q) => ({
                  value: q,
                  label: q === 'Other' ? 'Other' : String(Number.parseInt(q, 10) * CHIPS_PER_BOX),
                }))}
                value={quantityChoice}
                onChange={(v) => setQuantityChoice(v as QuantityChoice)}
              />
              {isOther && (
                <div className={styles.customQuantityRow}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={decrementCustom}
                    disabled={customBoxCount <= CUSTOM_BOX_MIN}
                    className={styles.customQuantityButton}
                    aria-label="Decrease quantity"
                  >
                    &minus;
                  </Button>
                  <span className={styles.customQuantityValue}>{customChipDisplay}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={incrementCustom}
                    disabled={customBoxCount >= CUSTOM_BOX_MAX}
                    className={styles.customQuantityButton}
                    aria-label="Increase quantity"
                  >
                    +
                  </Button>
                </div>
              )}
            </PortalField>

            <div className={styles.orderSectionHeading}>Shipping Address</div>
            <FormGrid>
              <PortalField
                label="Street Address *"
                fullWidth
                error="Please provide a street address"
                showError={showErrors && errors.street}
              >
                <PortalInput
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="123 Main Street"
                  hasError={showErrors && errors.street}
                />
              </PortalField>
              <PortalField label="Apt / Suite">
                <PortalInput value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt / Suite" />
              </PortalField>
              <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.city}>
                <PortalInput
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  hasError={showErrors && errors.city}
                />
              </PortalField>
              <PortalField label="State *" error="Please select a state" showError={showErrors && errors.state}>
                <PortalSelect
                  value={usState}
                  onChange={(e) => setUsState(e.target.value)}
                  hasError={showErrors && errors.state}
                >
                  <option value="">Select…</option>
                  {US_STATE_OPTIONS.map((s) => (
                    <option key={s.abbreviation} value={s.abbreviation}>
                      {s.name}
                    </option>
                  ))}
                </PortalSelect>
              </PortalField>
              <PortalField label="Zip Code *" error="Please provide a zip code" showError={showErrors && errors.zip}>
                <PortalInput
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="ZIP"
                  inputMode="numeric"
                  hasError={showErrors && errors.zip}
                />
              </PortalField>
            </FormGrid>

            <PortalField label="Notes">
              <PortalTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — e.g., event coming up, urgent need"
              />
            </PortalField>
          </>
        )}

        {submitError && <div className={styles.errorContainer}>{submitError}</div>}
      </div>

      <div className={`${stepStyles.orderStepFooter} ${stepStyles.orderStepFooterWithSupport}`}>
        <div className={stepStyles.support}>
          Have questions? <a href={`mailto:${SUPPORT_EMAIL}?subject=Fi%20Nano%20Order%20Questions`}>Contact support</a>
        </div>
        <ModalActions>{onDocuments ? documentsActions : detailsActions}</ModalActions>
      </div>
    </Modal>
  );
}
